<template>
  <v-row no-gutters>
    <v-col
      class="mr-md-12 pa-3 pa-md-0 mt-md-16 mt-12"
      :class="{ 'flex-1-1-100': $vuetify.display.mdAndDown }"
    >
      <div v-if="allTicketSelectionData && localSelectionsMap" class="d-lg-none mb-8">
        <v-row no-gutters justify="center" class="mb-6">
          <h2 class="fs-24">SELECT YOUR REGISTRATION</h2>
        </v-row>
        <TicketCard
          v-for="ticket of visibleTicketSelection"
          :key="ticket.ticket_id"
          :event-data="eventData"
          :ticket="ticket"
          :selected-tickets="localSelectionsMap"
          :qty="localSelectionsMap[`${ticket.ticket_id}`]?.qty || 0"
          @update:qty="(qty, revert) => handleQtyChange(ticket, qty, revert)"
        />
        <div v-if="allTicketSelectionData.length > ticketsToShow">
          <v-btn
            block
            color="#A3A3A3"
            variant="text"
            class="mt-2 text-transform-none"
            @click.stop="ticketsToShow += 2"
            append-icon="mdi-chevron-down"
          >
            Show more
          </v-btn>
        </div>
        <v-divider class="my-8"></v-divider>
      </div>

      <div v-if="isEmpty(tickets)">
        <v-card variant="outlined" style="border: 1px solid #e5e5e5">
          <v-card-text class="pa-2 text-center">
            <h2 class="fs-18 font-weight-medium" style="color: #262626">
              Please make a selection to begin
            </h2>
          </v-card-text>
        </v-card>
      </div>

      <v-expansion-panels v-else v-model="openExpansion" multiple>
        <template v-if="tickets.length > 0">
          <v-expansion-panel class="mb-6" :value="0">
            <v-expansion-panel-title>
              <v-row no-gutters>
                <h2 class="fs-18 font-weight-medium mb-2">Attendee Details</h2>
              </v-row>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="pa-0 pa-md-4">
                <v-col md="10" class="d-flex align-start flex-column ga-4">
                  <p class="fs-16">
                    Enter the Primary Registrant's information. They will receive a confirmation
                    email containing their registration details and guest list.
                  </p>
                  <div class="d-flex align-left items-center justify-start">
                    <div class="mr-3">
                      <v-tooltip location="top">
                        <template #activator="{ props }">
                          <v-icon
                            v-bind="props"
                            :color="
                              !!stashedData[primaryRegistrantTicket?._instanceId]
                                ? 'success'
                                : 'warning'
                            "
                            size="small"
                          >
                            {{
                              !!stashedData[primaryRegistrantTicket?._instanceId]
                                ? 'mdi-check-circle'
                                : 'mdi-alert-circle'
                            }}
                          </v-icon>
                        </template>
                        <span>
                          {{
                            !!stashedData[primaryRegistrantTicket?._instanceId]
                              ? 'Form Completed'
                              : 'Form Not Completed'
                          }}
                        </span>
                      </v-tooltip>
                    </div>
                    <p class="fs-16 font-italic">Primary Registrant:</p>
                  </div>
                  <div v-if="hasMultipleTicketTypes" class="w-100">
                    <p class="fs-16 font-weight-light-bold">
                      Select Registration for Primary Registrant:
                    </p>
                    <v-select
                      v-model="primaryRegistrantSelectionKey"
                      :items="primaryRegistrantDropdownItems"
                      item-title="title"
                      item-value="value"
                      variant="outlined"
                      class="mb-4"
                      hide-details
                      density="compact"
                    >
                      <template #item="{ props, item }">
                        <v-list-item
                          v-bind="props"
                          :title="item.raw.title"
                          :disabled="item.raw.disabled"
                        ></v-list-item>
                      </template>
                    </v-select>
                  </div>
                </v-col>
                <v-expansion-panels v-model="openChildPanel" multiple>
                  <v-expansion-panel :value="primaryRegistrantTicket?._instanceId">
                    <v-expansion-panel-title>
                      <v-row no-gutters>
                        <v-col>
                          <h2 class="mb-2 fs-16 font-weight-light-bold">
                            <template
                              v-if="
                                getActiveTicketPricing(primaryRegistrantTicket).type !== 'regular'
                              "
                            >
                              <span> {{ primaryRegistrantTicket?.name }}</span>
                              <span
                                class="ml-2"
                                style="text-decoration: line-through; font-weight: 400 !important"
                              >
                                {{
                                  Currency.formatCents(
                                    getActiveTicketPricing(primaryRegistrantTicket).originalPrice,
                                  )
                                }}
                              </span>
                              <span class="ml-2 font-weight-bold">
                                {{
                                  Currency.formatCents(
                                    getActiveTicketPricing(primaryRegistrantTicket).price,
                                  )
                                }}
                              </span>
                              <v-chip size="small" class="ml-2" variant="outlined" color="#F36E21">
                                {{ getActiveTicketPricing(primaryRegistrantTicket).label }}
                              </v-chip>
                            </template>
                            <span v-else>
                              {{ displayTicketName(primaryRegistrantTicket) }}
                            </span>
                          </h2>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <NewRegistrationForm
                        v-if="
                          primaryRegistrantTicket &&
                          ticketMap &&
                          ticketMap[primaryRegistrantTicket.ticket_id]
                        "
                        @save-registration="
                          handleRegistrationSave($event, primaryRegistrantTicket._instanceId)
                        "
                        @stash-state="handleStashState($event, primaryRegistrantTicket._instanceId)"
                        :eventData="eventData"
                        :isGroupHostCheckoutFlow="false"
                        :ticket-config="ticketMap[primaryRegistrantTicket.ticket_id]"
                        :saved-data="stashedData[primaryRegistrantTicket._instanceId]"
                        :allCustomQuestions="allCustomQuestions"
                        :allMealChoices="allMealChoices"
                        :showHostReferral="
                          eventData?.allow_host_referral && !route?.query?.invite_code
                        "
                        :hostItems="hostItems"
                        :isPrimary="true"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </template>

        <template v-if="additionalRegistrantTickets.length > 0">
          <v-expansion-panel class="mb-6" :value="1">
            <v-expansion-panel-title>
              <v-row no-gutters>
                <v-col>
                  <h2 class="fs-18 font-weight-medium mb-2">Additional Registrations</h2>
                </v-col>
              </v-row>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="pa-0 pa-md-4">
              <template
                v-for="(ticket, i) in additionalRegistrantTickets"
                :key="ticket._instanceId"
              >
                <div class="mt-4">
                  <v-row no-gutters justify="space-between" align="center">
                    <div class="d-flex align-left items-center justify-start">
                      <div class="mr-3">
                        <v-tooltip location="top">
                          <template #activator="{ props }">
                            <v-icon
                              v-bind="props"
                              :color="!!stashedData[ticket._instanceId] ? 'success' : 'warning'"
                              size="small"
                            >
                              {{
                                !!stashedData[ticket._instanceId]
                                  ? 'mdi-check-circle'
                                  : 'mdi-alert-circle'
                              }}
                            </v-icon>
                          </template>
                          <span>
                            {{
                              !!stashedData[ticket._instanceId]
                                ? 'Form Completed'
                                : 'Form Not Completed'
                            }}
                          </span>
                        </v-tooltip>
                      </div>
                      <h2 class="mb-4 fs-16 font-weight-medium font-italic">Guest {{ i + 2 }}:</h2>
                    </div>
                    <v-btn
                      icon="mdi-minus-circle"
                      size="small"
                      color="#DC2626"
                      variant="text"
                      style="box-shadow: none"
                      @click.stop="handleDeleteGuest(ticket)"
                    ></v-btn>
                  </v-row>

                  <v-expansion-panels v-model="openChildPanel" multiple>
                    <v-expansion-panel :value="ticket._instanceId" class="mt-2 mb-6">
                      <v-expansion-panel-title>
                        <v-row no-gutters>
                          <v-col>
                            <h2 class="mb-2 fs-16 font-weight-bold">
                              <template v-if="getActiveTicketPricing(ticket).type !== 'regular'">
                                <span> {{ ticket?.name }}</span>
                                <span
                                  class="ml-2"
                                  style="text-decoration: line-through; font-weight: 400 !important"
                                >
                                  {{
                                    Currency.formatCents(
                                      getActiveTicketPricing(ticket).originalPrice,
                                    )
                                  }}
                                </span>
                                <span class="ml-2 font-weight-bold">
                                  {{ Currency.formatCents(getActiveTicketPricing(ticket).price) }}
                                </span>
                                <v-chip
                                  size="small"
                                  class="ml-2"
                                  variant="outlined"
                                  color="#F36E21"
                                >
                                  {{ getActiveTicketPricing(ticket).label }}
                                </v-chip>
                              </template>
                              <span v-else>
                                {{ displayTicketName(ticket) }}
                              </span>
                            </h2>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <NewRegistrationForm
                          v-if="!ticket.table_id && ticketMap && ticketMap[ticket.ticket_id]"
                          @save-registration="handleRegistrationSave($event, ticket._instanceId)"
                          @stash-state="handleStashState($event, ticket._instanceId)"
                          :eventData="eventData"
                          :isGroupHostCheckoutFlow="false"
                          :ticket-config="ticketMap[ticket.ticket_id]"
                          :saved-data="stashedData[ticket._instanceId]"
                          :copy-source-data="stashedData[primaryRegistrantTicket?._instanceId]"
                          :copy-source-registration="
                            registrations[primaryRegistrantTicket?._instanceId]
                          "
                          copy-button-label="Copy from primary"
                          :allCustomQuestions="allCustomQuestions"
                          :allMealChoices="allMealChoices"
                          :isPrimary="false"
                        />
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </template>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </template>
      </v-expansion-panels>
    </v-col>

    <v-col class="pa-3" sm="12" md="4">
      <div v-if="allTicketSelectionData && localSelectionsMap" class="d-none d-lg-block">
        <v-row no-gutters justify="center" class="mb-5">
          <h2 class="fs-24">SELECT YOUR REGISTRATION</h2>
        </v-row>
        <TicketCard
          v-for="ticket of visibleTicketSelection"
          :key="ticket.ticket_id"
          :event-data="eventData"
          :ticket="ticket"
          :selected-tickets="localSelectionsMap"
          :qty="localSelectionsMap[`${ticket.ticket_id}`]?.qty || 0"
          @update:qty="(qty, revert) => handleQtyChange(ticket, qty, revert)"
        />
        <div v-if="allTicketSelectionData.length > ticketsToShow">
          <v-btn
            block
            color="#A3A3A3"
            variant="text"
            class="mt-2 text-transform-none"
            @click.stop="ticketsToShow += 2"
            append-icon="mdi-chevron-down"
          >
            Show more
          </v-btn>
        </div>
      </div>
      <v-divider class="my-8"></v-divider>

      <div class="mb-8" v-if="!isEmpty(tickets)">
        <div class="d-flex justify-space-between align-center mb-2">
          <p class="fs-14 font-weight-medium">Registrations completed</p>
          <p class="fs-14 font-weight-medium">
            {{ completedRegistrationsCount }} of {{ tickets.length }}
          </p>
        </div>
        <v-progress-linear
          :model-value="registrationProgress"
          color="#4CB848"
          bg-color="#E5E5E5"
          height="16"
          rounded
        ></v-progress-linear>
      </div>

      <RegistrationSummary
        v-if="
          displayTicketSummary?.length ||
          (displayAddOnSummary && Object.keys(displayAddOnSummary).length)
        "
        :displayTicketSummary="displayTicketSummary"
        :displayAddOnSummary="displayAddOnSummary"
        :eventData="eventData"
        :registrations="Object.values(registrations)"
        :disableContinue="disableContinue"
        :onContinue="(opts) => handleContinueToCheckout({ ...opts, invite_code: selectedHostId })"
      />
    </v-col>
  </v-row>

  <BaseModal
    :modal-open="openDeleteStashedFormConfirm"
    max-width="26%"
    :hide-header="true"
    :custom-actions="false"
    persistent
  >
    <template v-slot:default>
      <v-row no-gutters>
        <v-col sm="2" class="d-flex justify-center pt-2">
          <img
            style="
              background-color: var(--fundeasy-colors-primary-100);
              border-radius: 24px;
              height: 40px;
              width: 40px;
            "
            :src="AlertIcon"
            alt="alert"
          />
        </v-col>
        <v-col class="pt-2">
          <div class="d-flex justify-space-between align-center mb-4">
            <h3
              style="font-weight: 500; font-size: 18px; color: var(--fundeasy-colors-neutral-900)"
            >
              Remove registration selection
            </h3>
            <v-btn
              @click.stop="openDeleteStashedFormConfirm = false"
              icon="mdi-close"
              size="larger"
              style="box-shadow: none"
            ></v-btn>
          </div>
          <p
            style="font-size: 14px; font-weight: 400; color: var(--fundeasy-colors-neutral-600)"
            class="mb-4"
          >
            Registrant Information has already been saved. If you remove your registration selection
            now, your data will be lost.
          </p>
          <p style="font-size: 14px; font-weight: 400; color: var(--fundeasy-colors-neutral-600)">
            Are you sure you want to continue?
          </p>
        </v-col>
      </v-row>
    </template>
    <template v-slot:buttons>
      <v-row no-gutters justify="end">
        <v-btn
          class="mr-2 text-transform-none"
          variant="text"
          color="success"
          @click.stop="openDeleteStashedFormConfirm = false"
          >No</v-btn
        >
        <v-btn
          elevation="1"
          variant="elevated"
          color="error"
          class="text-transform-none"
          @click.stop="handleDeleteStashedForm"
          >Yes, delete</v-btn
        >
      </v-row>
    </template>
  </BaseModal>
</template>

<script setup>
import BaseModal from '@/components/modals/BaseModal';
import isEmpty from 'lodash/isEmpty';
import { computed, ref, watch } from 'vue';
import AlertIcon from '@/assets/Icons/AlertIcon.svg';
import { useEventGuestRegistration } from '@/composables/attendance/useEventGuestRegistration';
import BaseUrl from '@/api/BaseUrl';
import Headers from '@/api/Headers';
import ENDPOINTS from '@/api/endpoints';
import { Currency } from '~/utils/currency';

const props = defineProps({
  allTicketSelectionData: {
    type: Array,
    default: () => [],
  },
  displayTicketName: {
    type: Function,
    required: true,
  },
  loadingTicketData: {
    type: Boolean,
    default: true,
  },
  ticketMap: {
    type: Object,
    default: () => ({}),
  },
  addonSummary: {
    type: Object,
    default: () => ({}),
  },
  postOrderDetails: {
    type: Function,
    required: true,
  },
  eventData: {
    type: Object,
    default: () => ({}),
  },
  route: {
    type: Object,
    required: true,
  },
  allCustomQuestions: {
    type: Array,
    default: () => [],
  },
  allMealChoices: {
    type: Array,
    default: () => [],
  },
});

const {
  // State
  localSelectionsMap,
  registrations,
  stashedData,
  primaryRegistrantSelectionKey,
  openDeleteStashedFormConfirm,

  // Computed
  tickets,
  primaryRegistrantTicket,
  additionalRegistrantTickets,
  hasMultipleTicketTypes,
  primaryRegistrantDropdownItems,
  displayAddOnSummary,
  displayTicketSummary,
  completedRegistrationsCount,
  registrationProgress,
  disableContinue,

  // Methods
  handleQtyChange,
  handleRegistrationSave: _handleRegistrationSave,
  handleStashState,
  handleDeleteGuest,
  handleDeleteStashedForm,
  handleContinueToCheckout,
} = useEventGuestRegistration(
  computed(() => props.allTicketSelectionData),
  computed(() => props.loadingTicketData),
  computed(() => props.addonSummary),
  props.postOrderDetails,
  props.route,
  computed(() => props.ticketMap),
);

const ticketsToShow = ref(3);
const openExpansion = ref(null);
const openChildPanel = ref([]);

const selectedHostId = ref(null);

function getActiveTicketPricing(ticket) {
  const regularPrice = Number(
    ticket?.regular_price ?? ticket?.config?.regular_price ?? ticket?.config?.price ?? 0,
  );

  if (ticket?.is_early_bird && ticket?.is_early_bird_active) {
    return {
      price: Number(ticket?.early_bird_amount ?? ticket?.config?.price ?? regularPrice),
      originalPrice: regularPrice,
      type: 'early_bird',
      label: 'Early Bird Price',
    };
  }

  if (ticket?.is_late_registration && ticket?.is_late_registration_active) {
    return {
      price: Number(ticket?.late_registration_price ?? ticket?.config?.price ?? regularPrice),
      originalPrice: regularPrice,
      type: 'late_registration',
      label: 'Late Registration Price',
    };
  }

  return {
    price: Number(ticket?.config?.price ?? regularPrice),
    originalPrice: regularPrice,
    type: 'regular',
    label: '',
  };
}

const { data: hostsData } = useFetch(
  () =>
    props.eventData?.allow_host_referral && !props.route?.query?.invite_code
      ? `${BaseUrl.get(ENDPOINTS.EVENTS.GET_ALL_HOSTS)}${ENDPOINTS.EVENTS.GET_ALL_HOSTS}?event_id=${props.route.params.id}`
      : null,
  { headers: Headers },
);

const hostItems = computed(() =>
  (hostsData.value ?? []).map((h) => ({
    title: `${h.event_registration.first_name} ${h.event_registration.last_name}`,
    value: h.invite_code,
  })),
);

const visibleTicketSelection = computed(() => {
  return props.allTicketSelectionData.slice(0, ticketsToShow.value);
});

watch(tickets, (newTickets) => {
  if (newTickets.length > 0 && openExpansion.value === null) {
    openExpansion.value = [0, 1]; // 0 = Attendee Details, 1 = Additional Registrations
    if (primaryRegistrantTicket.value) {
      openChildPanel.value = [primaryRegistrantTicket.value._instanceId];
    }
  } else if (newTickets.length === 0) {
    openExpansion.value = null;
    openChildPanel.value = [];
  }
});

watch(primaryRegistrantTicket, (newTicket) => {
  if (newTicket) {
    if (!openChildPanel.value.includes(newTicket._instanceId)) {
      openChildPanel.value = [newTicket._instanceId];
    }
  }
});

const handleRegistrationSave = (ticketRegistration, instanceId) => {
  if (instanceId === primaryRegistrantTicket.value?._instanceId) {
    selectedHostId.value = ticketRegistration.selectedHostInviteCode ?? selectedHostId.value;
  }
  const { selectedHostInviteCode, ...registrationData } = ticketRegistration;
  const saveSuccess = _handleRegistrationSave(registrationData, instanceId);

  if (saveSuccess) {
    const currentIndex = tickets.value.findIndex((t) => t._instanceId === instanceId);
    if (currentIndex > -1 && currentIndex + 1 < tickets.value.length) {
      const nextInstanceId = tickets.value[currentIndex + 1]._instanceId;
      openChildPanel.value = [nextInstanceId];
    } else {
      openChildPanel.value = [];
    }
  }
};
</script>
